import React, { useMemo, useState } from "react";
import { ChevronDown, ChevronUp, Search } from "lucide-react";
import { useApp } from "@/context/AppContext";

export function PageHeader({ title, subtitle, actions }) {
  return (
    <div className="mb-6 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-[var(--ink)] md:text-3xl" data-testid="page-title">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-[var(--muted-ink)]">{subtitle}</p>}
      </div>
      {actions && <div className="no-print flex flex-wrap gap-2">{actions}</div>}
    </div>
  );
}

const TONES = {
  default: "border-l-[#283F91]", warn: "border-l-amber-500", danger: "border-l-red-600", ok: "border-l-emerald-600", muted: "border-l-[#747984]",
};

export function StatCard({ label, value, hint, tone = "default", testid, onClick }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`panel border-l-4 ${TONES[tone]} p-4 text-left transition-shadow hover:shadow-md ${onClick ? "cursor-pointer" : "cursor-default"}`}>
      <div className="text-[11px] font-semibold uppercase tracking-wider text-[var(--muted-ink)]">{label}</div>
      <div className="mt-2 text-2xl font-bold text-[var(--ink)]" data-testid={testid ? `${testid}-value` : undefined}>{value ?? "—"}</div>
      {hint && <div className="mt-1 text-[11px] text-[var(--muted-ink)]">{hint}</div>}
    </button>
  );
}

const PASTEL = {
  blue: ["bg-[#EAF4FF]", "bg-[#283F91] text-white"], green: ["bg-[#E7F6EE]", "bg-emerald-600 text-white"],
  lavender: ["bg-[#F0ECFB]", "bg-[#6D5BD0] text-white"], amber: ["bg-[#FFF5DF]", "bg-amber-500 text-white"],
};

export function PastelCard({ icon: Icon, label, value, hint, tone = "blue", testid }) {
  const [bg, ic] = PASTEL[tone];
  return (
    <div className={`${bg} flex items-center gap-3 rounded-2xl p-4 dark:bg-white/5`} data-testid={testid}>
      <div className={`${ic} flex h-11 w-11 shrink-0 items-center justify-center rounded-xl`}><Icon size={20} /></div>
      <div className="min-w-0">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-[#4A5578] dark:text-gray-300">{label}</div>
        <div className="truncate text-2xl font-bold text-[#20336F] dark:text-blue-200" data-testid={testid ? `${testid}-value` : undefined}>{value ?? "—"}</div>
        {hint && <div className="truncate text-[11px] text-[#4A5578] dark:text-gray-400">{hint}</div>}
      </div>
    </div>
  );
}

export function Tabs({ tabs, value, onChange, testid }) {
  return (
    <div className="mb-5 flex gap-1 overflow-x-auto rounded-xl border bg-[#EAF4FF] p-1 dark:bg-white/5" data-testid={testid}>
      {tabs.map(({ key, label, icon: Icon }) => (
        <button key={key} onClick={() => onChange(key)} data-testid={`${testid}-${key}`}
          className={`flex shrink-0 items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${value === key ? "bg-white text-[#20336F] shadow-sm dark:bg-[var(--panel)] dark:text-blue-200" : "text-[var(--muted-ink)] hover:text-[#20336F]"}`}>
          {Icon && <Icon size={15} />}{label}
        </button>
      ))}
    </div>
  );
}

const IND = {
  Current: "bg-emerald-50 text-emerald-700 border-emerald-200", Upcoming: "bg-blue-50 text-[#283F91] border-blue-200",
  Overdue: "bg-red-50 text-red-700 border-red-200", "Missed Appointment": "bg-rose-50 text-rose-700 border-rose-200",
  "Pending Pharmacy Confirmation": "bg-amber-50 text-amber-800 border-amber-200", "Needs Follow-up": "bg-orange-50 text-orange-700 border-orange-200",
  Completed: "bg-emerald-50 text-emerald-700 border-emerald-200", Administered: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Filled: "bg-emerald-50 text-emerald-700 border-emerald-200", Retained: "bg-emerald-50 text-emerald-700 border-emerald-200",
  "No Show": "bg-rose-50 text-rose-700 border-rose-200", Sent: "bg-amber-50 text-amber-800 border-amber-200",
  Pending: "bg-amber-50 text-amber-800 border-amber-200", "In Progress": "bg-blue-50 text-[#283F91] border-blue-200",
  Scheduled: "bg-blue-50 text-[#283F91] border-blue-200", Confirmed: "bg-blue-50 text-[#283F91] border-blue-200",
  "Not Retained": "bg-red-50 text-red-700 border-red-200", ACTIVE: "bg-emerald-50 text-emerald-700 border-emerald-200",
  High: "bg-red-50 text-red-700 border-red-200", Missed: "bg-rose-50 text-rose-700 border-rose-200",
  DISCHARGED: "bg-[#FDECEC] text-red-700 border-red-200", "RE-ENGAGE": "bg-[#FFF5DF] text-amber-800 border-amber-200",
  Assigned: "bg-[#F0ECFB] text-[#5B47C2] border-violet-200", Cancelled: "bg-gray-100 text-gray-600 border-gray-200",
  Available: "bg-emerald-50 text-emerald-700 border-emerald-200", Busy: "bg-amber-50 text-amber-800 border-amber-200",
  Unavailable: "bg-red-50 text-red-700 border-red-200", Paused: "bg-gray-100 text-gray-600 border-gray-200",
};
const SLABEL = { ACTIVE: "Active", DISCHARGED: "Discharged", "RE-ENGAGE": "Re-Engage" };

export function Badge({ value, testid }) {
  const { t } = useApp();
  if (!value) return <span className="text-[var(--muted-ink)]">—</span>;
  return (
    <span data-testid={testid} className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${IND[value] || "border-gray-200 bg-gray-50 text-gray-600 dark:bg-white/5 dark:text-gray-300"}`}>
      {SLABEL[value] || t(value)}
    </span>
  );
}

export function Field({ label, children, required, className = "" }) {
  return (
    <label className={`flex flex-col gap-1.5 ${className}`}>
      <span className="lbl">{label}{required && <span className="text-red-600"> *</span>}</span>
      {children}
    </label>
  );
}

export function Select({ value, onChange, options, placeholder, testid, className = "", disabled }) {
  return (
    <select className={`inp ${className}`} value={value ?? ""} disabled={disabled} onChange={(e) => onChange(e.target.value)} data-testid={testid}>
      {placeholder !== undefined && <option value="">{placeholder}</option>}
      {options.map((o) => (typeof o === "string" ? <option key={o} value={o}>{o}</option> : <option key={o.value} value={o.value}>{o.label}</option>))}
    </select>
  );
}

export function DataTable({ columns, rows, testid = "data-table", onRowClick, pageSize = 25, searchable = true, toolbar, empty = "No records found", rowKey = "id" }) {
  const { t } = useApp();
  const [q, setQ] = useState("");
  const [sort, setSort] = useState({ key: null, dir: 1 });
  const [page, setPage] = useState(0);
  const filtered = useMemo(() => {
    let r = rows || [];
    if (q) {
      const s = q.toLowerCase();
      r = r.filter((x) => columns.some((c) => String(c.text ? c.text(x) : x[c.key] ?? "").toLowerCase().includes(s)));
    }
    if (sort.key) {
      const col = columns.find((c) => c.key === sort.key);
      const val = (x) => (col?.text ? col.text(x) : x[sort.key]);
      r = [...r].sort((a, b) => {
        const va = val(a), vb = val(b);
        if (va == null) return 1;
        if (vb == null) return -1;
        return (va > vb ? 1 : va < vb ? -1 : 0) * sort.dir;
      });
    }
    return r;
  }, [rows, q, sort, columns]);
  const pages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const view = filtered.slice(page * pageSize, page * pageSize + pageSize);
  return (
    <div className="panel overflow-hidden" data-testid={testid}>
      {(searchable || toolbar) && (
        <div className="no-print flex flex-col gap-2 border-b p-3 md:flex-row md:items-center">
          {searchable && (
            <div className="relative md:w-72">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted-ink)]" />
              <input className="inp pl-8" placeholder={t("Search")} value={q} onChange={(e) => { setQ(e.target.value); setPage(0); }} data-testid={`${testid}-search`} />
            </div>
          )}
          <div className="flex flex-1 flex-wrap items-center gap-2">{toolbar}</div>
          <div className="text-xs text-[var(--muted-ink)]" data-testid={`${testid}-count`}>{filtered.length} records</div>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="tbl w-full">
          <thead>
            <tr>
              {columns.map((c) => (
                <th key={c.key} className={c.sortable === false ? "" : "cursor-pointer select-none"}
                  onClick={() => c.sortable !== false && setSort((s) => ({ key: c.key, dir: s.key === c.key ? -s.dir : 1 }))}>
                  <span className="inline-flex items-center gap-1">{t(c.label)}
                    {sort.key === c.key && (sort.dir === 1 ? <ChevronUp size={12} /> : <ChevronDown size={12} />)}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {view.map((r, i) => (
              <tr key={r[rowKey] || i} onClick={onRowClick ? () => onRowClick(r) : undefined} className={onRowClick ? "cursor-pointer" : ""} data-testid={`${testid}-row`}>
                {columns.map((c) => <td key={c.key}>{c.render ? c.render(r) : (r[c.key] ?? "—")}</td>)}
              </tr>
            ))}
            {!view.length && <tr><td colSpan={columns.length} className="py-10 text-center text-[var(--muted-ink)]">{empty}</td></tr>}
          </tbody>
        </table>
      </div>
      {pages > 1 && (
        <div className="no-print flex items-center justify-end gap-2 border-t p-2 text-xs">
          <button className="btn btn-ghost btn-sm" disabled={page === 0} onClick={() => setPage(page - 1)} data-testid={`${testid}-prev`}>Prev</button>
          <span>{page + 1} / {pages}</span>
          <button className="btn btn-ghost btn-sm" disabled={page >= pages - 1} onClick={() => setPage(page + 1)} data-testid={`${testid}-next`}>Next</button>
        </div>
      )}
    </div>
  );
}

const iso = (d) => d.toISOString().slice(0, 10);
export function rangeFor(preset) {
  const n = new Date();
  const y = n.getFullYear(), m = n.getMonth();
  if (preset === "last_month") return [iso(new Date(Date.UTC(y, m - 1, 1))), iso(new Date(Date.UTC(y, m, 0)))];
  if (preset === "this_quarter") return [iso(new Date(Date.UTC(y, Math.floor(m / 3) * 3, 1))), iso(n)];
  if (preset === "this_year") return [`${y}-01-01`, iso(n)];
  return [iso(new Date(Date.UTC(y, m, 1))), iso(n)];
}

export function DateRange({ value, onChange }) {
  const { t } = useApp();
  const presets = [["this_month", "This Month"], ["last_month", "Last Month"], ["this_quarter", "This Quarter"], ["this_year", "This Year"], ["custom", "Custom Date Range"]];
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="date-range">
      <div className="flex flex-wrap overflow-hidden rounded-md border">
        {presets.map(([k, l]) => (
          <button key={k} data-testid={`range-${k}`} onClick={() => onChange(k === "custom" ? { ...value, preset: k } : { preset: k, start: rangeFor(k)[0], end: rangeFor(k)[1] })}
            className={`px-3 py-1.5 text-xs font-semibold transition-colors ${value.preset === k ? "bg-[#283F91] text-white" : "bg-[var(--panel)] text-[var(--muted-ink)] hover:text-[#283F91]"}`}>{t(l)}</button>
        ))}
      </div>
      {value.preset === "custom" && (
        <>
          <input type="date" className="inp w-40" value={value.start} onChange={(e) => onChange({ ...value, start: e.target.value })} data-testid="range-start" />
          <input type="date" className="inp w-40" value={value.end} onChange={(e) => onChange({ ...value, end: e.target.value })} data-testid="range-end" />
        </>
      )}
    </div>
  );
}

export const initialRange = (p = "this_month") => ({ preset: p, start: rangeFor(p)[0], end: rangeFor(p)[1] });
